import { useCallback, useRef } from 'react';

const REDRAW = { lazyUpdate: true, silent: true };
// Room past the plot for the pill of a tick at the very edge.
const EDGE_ROOM = 28;
const LEGEND_CHANGES = ['legendselectchanged', 'legendselectall', 'legendinverseselect'];

/**
 * A chart's selection lives on its canvas: hovering never renders React. Takes
 * `{view, count, initial, keyboard, describe(index), patch()}` or nothing, and
 * returns the renderer's `bind`. One canvas can outlive its selection, gain a
 * new one or lose it when a filter changes the chart's shape, so every handler
 * reads the latest and an earlier pin dies with the selection it was made on.
 * `stacks(shown)`, on a stacked chart, gives what to redraw for the legend's state.
 */
export function useChartSelection(selection, stacks) {
  const live = useRef(selection);
  const liveStacks = useRef(stacks);
  const width = useRef(0);
  live.current = selection;
  liveStacks.current = stacks;
  if (selection) selection.view.width = width.current;

  return useCallback((instance, element) => {
    width.current = instance.getWidth();
    let pinned = null;
    let pinnedTo = null;
    let hovered = null;
    let fitted = null;

    const redraw = (current) => instance.setOption(current.patch(), REDRAW);
    /** What is on screen: the zoom window, which option building cannot know. */
    const measure = (current) => {
      current.view.width = width.current;
      const extent = instance.getModel?.()?.getComponent('xAxis', 0)?.axis?.scale?.getExtent();
      if (!extent) return;
      [current.view.first] = extent;
      current.view.visible = extent[1] - extent[0] + 1;
    };
    /**
     * A grid sized by its labels moves the whole plot whenever a tick at the
     * edge changes, so the first draw's margins hold for the selection's life.
     */
    const holdLayout = (current) => {
      if (!current.holdsLayout || current.view.grid) return;
      const plot = instance.getModel?.()?.getComponent('grid', 0)?.coordinateSystem?.getRect?.();
      if (!plot) return;
      current.patch();
      const { tickLines = 1, firstTickLines = 1, tickLine = 0 } = current.view;
      current.view.grid = {
        containLabel: false,
        left: plot.x,
        top: plot.y,
        right: Math.max(instance.getWidth() - plot.x - plot.width, EDGE_ROOM),
        // Ticks can wrap more at the real width than the first draw assumed.
        bottom: instance.getHeight() - plot.y - plot.height + Math.max(0, tickLines - firstTickLines) * tickLine,
      };
    };
    const announce = (current) => {
      element.setAttribute('aria-valuemax', String(current.count - 1));
      element.setAttribute('aria-valuenow', String(current.view.selected));
      element.setAttribute('aria-valuetext', current.describe(current.view.selected));
    };
    const syncRole = () => {
      const current = live.current;
      if (!current?.keyboard || current.view.selected < 0) {
        ['tabindex', 'aria-valuemin', 'aria-valuemax', 'aria-valuenow', 'aria-valuetext'].forEach((name) => element.removeAttribute(name));
        return;
      }
      // The engine's accessibility layer rewrites the role on every draw.
      element.setAttribute('role', 'slider');
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-valuemin', '0');
      announce(current);
    };
    const show = (index) => {
      const current = live.current;
      if (!current) return;
      const held = pinnedTo === current ? pinned : null;
      const next = index ?? held ?? current.initial;
      if (next === current.view.selected) return;
      current.view.selected = next;
      redraw(current);
      syncRole();
    };
    const pin = (index) => {
      pinned = index;
      pinnedTo = live.current;
    };
    // The column's whole height answers, even above a short or missing bar.
    const indexAt = (x) => {
      const current = live.current;
      if (!current || !instance.containPixel('grid', [x, instance.getHeight() / 2])) return null;
      const index = Math.round(Number(instance.convertFromPixel({ xAxisIndex: 0 }, x)));
      return Math.max(0, Math.min(current.count - 1, index));
    };
    const restack = ({ selected }) => {
      const patch = liveStacks.current?.((name) => selected[name] !== false);
      if (patch) instance.setOption(patch, REDRAW);
    };
    const zr = instance.getZr();
    zr.on('mousemove', (event) => {
      const index = indexAt(event.offsetX);
      if (live.current?.keyboard) zr.setCursorStyle(index === null ? 'default' : 'pointer');
      if (index === hovered) return;
      hovered = index;
      show(index);
    });
    zr.on('globalout', () => {
      hovered = null;
      show(null);
    });
    zr.on('click', (event) => {
      const index = indexAt(event.offsetX);
      if (index === null || !live.current?.keyboard) return;
      pin(index);
      show(index);
    });
    const onKeyDown = (event) => {
      const current = live.current;
      if (!current?.keyboard) return;
      const { count, view } = current;
      const moves = { ArrowLeft: view.selected - 1, ArrowRight: view.selected + 1, Home: 0, End: count - 1 };
      if (!(event.key in moves)) return;
      event.preventDefault();
      const index = Math.max(0, Math.min(count - 1, moves[event.key]));
      pin(index);
      show(index);
    };
    element.addEventListener('keydown', onKeyDown);
    instance.on('rendered', syncRole);
    LEGEND_CHANGES.forEach((name) => instance.on(name, restack));
    instance.on('datazoom', () => {
      const current = live.current;
      if (!current) return;
      measure(current);
      redraw(current);
    });
    syncRole();
    return {
      // An option is built before its canvas is measured. The engine drops a
      // setOption made from inside its own draw, so this runs after that draw.
      drawn: () => {
        const current = live.current;
        if (!current || fitted === current) return;
        fitted = current;
        measure(current);
        holdLayout(current);
        redraw(current);
      },
      resized: () => {
        width.current = instance.getWidth();
        const current = live.current;
        if (!current) return;
        measure(current);
        redraw(current);
      },
      dispose: () => element.removeEventListener('keydown', onKeyDown),
    };
  }, []);
}
